import { getSaveDate } from '@user/customer/modals/Account/Invoice/useInvoiceForm'
import type { CreateCustomerData, ParentData } from '../schemas'
import { firstDefined, getRegionCountryCode } from './helpers'
import { isKidsAccount } from './isKids'

type AddressFields = {
  street: string
  street2: string
  city: string
  zip: string
  country_code: string
}

type InvoiceFormDefaults = AddressFields & {
  firstName: string
  lastName: string
  email: string
  mobile: string
  birth_date: string | null
  vatId: string
  type: 'invoice'
}

const withInvoiceDefaults = (
  values: Omit<InvoiceFormDefaults, 'vatId' | 'type'>,
  invoice: CreateCustomerData['invoice']
): InvoiceFormDefaults => ({
  ...values,
  vatId: firstDefined(invoice?.vatId, invoice?.vat),
  type: 'invoice',
})

const addressFromParent = (
  invoice: CreateCustomerData['invoice'],
  parent: ParentData | undefined,
  fallbackCountryCode: string
): AddressFields => ({
  street: firstDefined(invoice?.street, parent?.street),
  street2: firstDefined(invoice?.street2, parent?.street2),
  city: firstDefined(invoice?.city, parent?.city),
  zip: firstDefined(invoice?.zip, parent?.zip),
  country_code: firstDefined(
    invoice?.country_code,
    parent?.country_code,
    fallbackCountryCode
  ),
})

const addressFromCustomer = (
  invoice: CreateCustomerData['invoice'],
  address: CreateCustomerData['address'] | undefined,
  fallbackCountryCode: string
): AddressFields => ({
  street: firstDefined(invoice?.street, address?.street),
  street2: firstDefined(invoice?.street2, address?.street2),
  city: firstDefined(invoice?.city, address?.city),
  zip: firstDefined(invoice?.zip, address?.zip),
  country_code: firstDefined(
    invoice?.country_code,
    address?.country_code,
    fallbackCountryCode
  ),
})

export const getInvoiceFormDefaults = (
  data: Partial<CreateCustomerData>,
  fallbackCountryCode = ''
): InvoiceFormDefaults => {
  const {
    invoice,
    first_name,
    last_name,
    birthday,
    email,
    mobile,
    address,
    parentData,
  } = data

  const countryCode = getRegionCountryCode(fallbackCountryCode)
  const kids = isKidsAccount(data)

  if (kids) {
    if (parentData) {
      const addr = addressFromParent(invoice, parentData, countryCode)
      return withInvoiceDefaults(
        {
          firstName: firstDefined(invoice?.firstName, parentData.first_name),
          lastName: firstDefined(invoice?.lastName, parentData.last_name),
          ...addr,
          email: firstDefined(invoice?.email, parentData.email, email),
          mobile: firstDefined(invoice?.mobile, parentData.mobile, mobile),
          birth_date: invoice?.birth_date ?? parentData.birth_date ?? null,
        },
        invoice
      )
    }

    const addr = addressFromCustomer(invoice, address, countryCode)
    return withInvoiceDefaults(
      {
        firstName: firstDefined(invoice?.firstName),
        lastName: firstDefined(invoice?.lastName),
        ...addr,
        email: firstDefined(invoice?.email, email),
        mobile: firstDefined(invoice?.mobile, mobile),
        birth_date: invoice?.birth_date ?? null,
      },
      invoice
    )
  }

  const addr = addressFromCustomer(invoice, address, countryCode)
  return withInvoiceDefaults(
    {
      firstName: firstDefined(invoice?.firstName, first_name),
      lastName: firstDefined(invoice?.lastName, last_name),
      ...addr,
      email: firstDefined(invoice?.email, email),
      mobile: firstDefined(invoice?.mobile, mobile),
      birth_date:
        invoice?.birth_date ?? (birthday ? getSaveDate(birthday) : null),
    },
    invoice
  )
}

export const getAddressFormDefaults = (
  data: Partial<CreateCustomerData>,
  fallbackCountryCode = ''
) => {
  const { address, parentData } = data
  const countryCode = getRegionCountryCode(fallbackCountryCode)

  return {
    street: firstDefined(address?.street, parentData?.street),
    street2: firstDefined(address?.street2, parentData?.street2),
    city: firstDefined(address?.city, parentData?.city),
    zip: firstDefined(address?.zip, parentData?.zip),
    country_code: firstDefined(
      address?.country_code,
      parentData?.country_code,
      countryCode
    ),
    vat: address?.vat || '',
  }
}
